import { useState, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import LockIcon from "@mui/icons-material/Lock";
import { Box, Typography, Skeleton } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "constants/colors";
import { useTranslation } from "react-i18next";

// 画像を合成する関数
const mergeImages = async (imageUrls) => {
  return new Promise(async (resolve, reject) => {
    try {
      const images = await Promise.all(imageUrls.map(loadImage));
      if (images.length === 0) return reject(new Error("画像がありません"));

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const width = images[0].width;
      const height = images[0].height;
      canvas.width = width;
      canvas.height = height;

      images.forEach((img) => ctx.drawImage(img, 0, 0, width, height));

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob)); // ✅ Blob URL を返す
        } else {
          reject(new Error("画像の生成に失敗しました"));
        }
      }, "image/png");
    } catch (error) {
      reject(error);
    }
  });
};

// 画像をロードする関数
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // CORS 対策
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
  });
};

// 画像をダウンロードする関数
const downloadImage = (imageUrl) => {
  const link = document.createElement("a");
  link.href = imageUrl;
  link.download = "merged_image.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// `ImageWithSaveButton` コンポーネント
export default function ChiImageWithSaveButton({
  imageUrls,
  showDownloadButton = false,
}) {
  const { t } = useTranslation(["chi"]);
  const [mergedImage, setMergedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const merge = async () => {
      try {
        setLoading(true);
        setError("");
        const mergedUrl = await mergeImages(imageUrls);
        setMergedImage(mergedUrl);
      } catch (err) {
        setError(t("chi:imageReward.displayError", {
          defaultValue: "報酬の表示ができませんでした",
        }));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (showDownloadButton === true) {
      setMergedImage(imageUrls[0]);
      setLoading(false);
    } else if (imageUrls.length > 0) {
      merge();
    } else {
      setLoading(false);
      setMergedImage(null);
    }
  }, [imageUrls, showDownloadButton, t]);

  if (!loading && !mergedImage) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="240px"
        width="100%"
        backgroundColor="rgba(255, 255, 255, 0.2)" // 黒い透明レイヤー
        backdropFilter="blur(16px)" // ここで背景をぼかす
        WebkitBackdropFilter="blur(16px)" // Safari対応
        borderRadius="4px"
      >
        {/* <Typography fontWeight="bold" color={COLORS.TEXT_BLACK} fontSize="15px">
          クリアしたクエストがありません
        </Typography> */}
        <LockIcon style={{ color: "white", fontSize: 50 }} />
      </Box>
    );
  }

  return (
    <>
      {loading && <Skeleton variant="rectangular" width="100%" height={240} />}
      {!loading && (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
            borderRadius: "6px",
          }}
        >
          {error && <p style={{ color: "red" }}>{error}</p>}
          {mergedImage && (
            <>
              <img
                src={mergedImage}
                alt={t("chi:imageReward.alt", { defaultValue: "Quest reward" })}
                style={{
                  width: "100%",
                  display: "block",
                  borderRadius: "4px",
                  filter: "drop-shadow(0px 0px 5px #666666)",
                }}
              />
              {/* 保存ボタン */}
              {showDownloadButton && (
                <IconButton
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "#ffffff",
                    borderRadius: "50%",
                    padding: 8,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.4)",
                  }}
                  aria-label={t("chi:imageReward.saveLabel", {
                    defaultValue: "Save image",
                  })}
                  onClick={() => downloadImage(mergedImage)}
                >
                  <FileDownloadIcon />
                </IconButton>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
